import { paddingToPx, resolveCustomPadding } from '../../utils/tableStyles';
import { defaultConfiguration } from '../../constants/constants';
import { paletteColor } from '../theme/palette';

/**
 * Schéma de style du Tableau (cf. shared/theme/styleSchema) : les réglages du
 * `design` du bloc du site, et comment le configurateur du classique range
 * chacun. Les deux formats diffèrent — noms (`customHeaderColor` /
 * `headerBgColor`), couleurs de palette ou CSS, interrupteurs du classique
 * (`customBorderBool`, `stripped1`…) — : chaque réglage porte ici sa lecture
 * et son écriture côte à côte, pour que l'aller-retour se tienne.
 *
 * Ce qu'un style de la galerie ne pose PAS, et pourquoi : les polices et
 * couleurs de texte (le texte riche les porte déjà, cellule par cellule ; un
 * style du thème, lui, les pose), le pied de tableau, l'en-tête fixe, les
 * espacements fins. Ce sont des choix de contenu ou de contexte — un
 * changement de style ne doit ni allumer ni éteindre le pied d'un tableau.
 */

const toColor = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value.style || fallback;
};

const bool = (value, fallback) => (typeof value === 'boolean' ? value : fallback);

const color = (classicKey) => ({
  read: (style, { fallback }) => toColor(style[classicKey], fallback),
  write: (value) => (value ? { [classicKey]: paletteColor(value) } : {}),
});

// Même nom des deux côtés : seule la lecture normalise.
const flag = (key) => ({ read: (style, { fallback }) => bool(style[key], fallback) });

const spacing = (key) => ({
  read: (style, { fallback }) => (style[key] != null ? String(style[key]) : fallback),
});

const renamed = (classicKey) => ({
  read: (style, { fallback }) => style[classicKey] || fallback,
  write: (value) => ({ [classicKey]: value }),
});

const documentFont = ({ fontFamily }) => fontFamily || defaultConfiguration.fontFamily;
const documentFontSize = ({ fontSize }) => fontSize || 14;

const webSizeFromPadding = (padding) => {
  if (padding <= 6) return 'small';
  if (padding <= 10) return 'middle';
  return 'default';
};

// Le classique n'allume ses couleurs de texte que derrière un interrupteur
// commun aux deux.
const textColorWrite = (classicKey) => (value, values) => ({
  customTextColorBool: !!(values.textColorEven || values.textColorOdd),
  [classicKey]: paletteColor(value) || '',
});

export const TABLE_STYLE_SCHEMA = {
  layout: { default: 'table', only: 'site' },
  cardHeader: { default: 'firstCell', seed: true, only: 'site' },
  showHeader: { default: true, seed: true, classic: flag('showHeader') },
  headerBgColor: { default: '#02509D', classic: color('customHeaderColor') },
  headerTextColor: { default: '#ffffff', classic: color('customHeaderTextColor') },
  headerUppercase: { default: false, only: 'site' },
  bordered: { default: true, classic: flag('bordered') },
  borderScope: { default: 'all', only: 'site' },
  useCustomBorder: {
    default: false,
    classic: {
      read: (style) => !!style.customBorderBool,
      write: (value) => ({ customBorderBool: !!value }),
    },
  },
  borderColor: { default: '#e2e8f0', classic: color('customBorderColor') },
  // Lus même interrupteur éteint : le site les règle à part (ses cartes les
  // appliquent sans lui).
  borderWidth: {
    default: 1,
    classic: {
      read: (style, { fallback }) => {
        const width = Number(style.customBorderWidth);
        return Number.isFinite(width) && width > 0 ? width : fallback;
      },
      write: (value) => ({ customBorderWidth: value }),
    },
  },
  borderStyle: { default: 'solid', classic: renamed('customBorderType') },
  cornerRadius: { default: 0, only: 'site' },
  isStripped: {
    default: true,
    classic: {
      read: (style) => !!(style.stripped1 || style.strippedCustomClrBool),
      write: (value) => ({ stripped1: !!value, strippedCustomClrBool: !!value }),
    },
  },
  // Le classique range le fond des lignes à deux endroits selon l'alternance ;
  // l'écrire aux deux le garde quand on bascule l'alternance.
  rowEvenBgColor: {
    default: 'transparent',
    classic: {
      read: (style, { fallback }) => (style.strippedCustomClrBool
        ? toColor(style.strippedCustomClr1, fallback)
        : toColor(style.customCellColor, fallback)),
      write: (value) => (value
        ? { strippedCustomClr1: paletteColor(value), customCellColor: paletteColor(value) }
        : {}),
    },
  },
  strippedColor: { default: '#f2f7fb', classic: color('strippedCustomClr2') },
  highlightOnHover: { default: true, classic: flag('highlightOnHover') },
  highlightOnHoverColor: {
    default: '',
    classic: {
      read: (style, { fallback }) => (style.highlightOnHoverColor != null
        ? String(style.highlightOnHoverColor)
        : fallback),
      write: (value) => (value ? { highlightOnHoverColor: value } : {}),
    },
  },
  // Le site n'a que les trois tailles : une taille personnalisée prend la plus
  // proche de son espacement (petite 4px, moyenne 8px, grande 12px).
  size: {
    default: 'default',
    classic: {
      read: (style, { fallback, fontSize }) => {
        if (style.size !== 'custom') return style.size || fallback;
        const padding = resolveCustomPadding(style);
        return webSizeFromPadding(paddingToPx(
          padding.value,
          padding.unit,
          Number(style.fontSizeCells) || documentFontSize({ fontSize }),
        ));
      },
      write: (value) => (value ? { size: value } : {}),
    },
  },
  firstColumnEmphasis: { default: false, seed: true, only: 'site' },

  // Posés par les styles du thème seulement.
  // `fontFamily` n'a pas d'équivalent dans le classique : il y passe tel quel,
  // et tient lieu des deux polices tant qu'elles manquent.
  fontFamily: {
    style: 'theme',
    default: documentFont,
    classic: {
      read: (style) => style.fontFamily,
      write: (value, values) => ({
        fontFamily: value,
        ...(values.headerFontFamily ? {} : { fontFamilyHeader: value }),
        ...(values.cellFontFamily ? {} : { fontFamilyCells: value }),
      }),
    },
  },
  headerFontFamily: { style: 'theme', default: documentFont, classic: renamed('fontFamilyHeader') },
  cellFontFamily: { style: 'theme', default: documentFont, classic: renamed('fontFamilyCells') },
  textColorEven: {
    style: 'theme',
    default: '',
    classic: {
      read: (style) => (style.customTextColorBool ? toColor(style.customTextColor1, '') : ''),
      write: textColorWrite('customTextColor1'),
    },
  },
  textColorOdd: {
    style: 'theme',
    default: '',
    classic: {
      read: (style) => (style.customTextColorBool || (style.stripped1 && style.alternateTextColors)
        ? toColor(style.customTextColor2, '')
        : ''),
      write: textColorWrite('customTextColor2'),
    },
  },

  // Recopiés depuis le document, jamais posés par un style.
  fontSize: { style: false, default: documentFontSize },
  headerFontSize: {
    style: false,
    default: documentFontSize,
    classic: {
      read: (style, { fallback }) => Number(style.fontSizeHeader) || fallback,
      write: (value) => ({ fontSizeHeader: value }),
    },
  },
  cellFontSize: {
    style: false,
    default: documentFontSize,
    classic: {
      read: (style, { fallback }) => Number(style.fontSizeCells) || fallback,
      write: (value) => ({ fontSizeCells: value }),
    },
  },
  footer: { style: false, default: false, classic: { read: (style) => !!style.footer } },
  footerData: { style: false, default: '', classic: { read: (style) => style.footerData || '' } },
  stickyHeader: { style: false, default: false, classic: flag('stickyHeader') },
  stickyHeaderOffset: {
    style: false,
    default: 0,
    classic: {
      read: (style, { fallback }) => (Number.isFinite(Number(style.stickyHeaderOffset))
        ? Number(style.stickyHeaderOffset)
        : fallback),
    },
  },
  horizontalSpacing: { style: false, default: '', classic: spacing('horizontalSpacing') },
  verticalSpacing: { style: false, default: '', classic: spacing('verticalSpacing') },
  // Lecture à l'écran : un PDF ne défile pas. Hauteur max en px (0 : le
  // tableau prend sa hauteur), colonnes de gauche figées au défilement latéral.
  maxHeight: { style: false, default: 0, only: 'site' },
  stickyColumns: { style: false, default: 0, only: 'site' },
  // Sous une largeur de téléphone : défiler, ou passer en cartes.
  mobileLayout: { style: false, default: 'scroll', only: 'site' },
  // Plancher de largeur d'une colonne (px) : en dessous, le tableau défile.
  minColumnWidth: { style: false, default: 100, only: 'site' },
  // Lecture interactive (cf. webviewer tableInteractions) : tri, recherche,
  // filtre sur une colonne (sa clé), groupes repliables, colonne survolée.
  // `sortColumns` : clés des colonnes triables, vide pour toutes.
  sortable: { style: false, default: false, only: 'site' },
  sortColumns: { style: false, default: [], only: 'site' },
  searchable: { style: false, default: false, only: 'site' },
  filterColumn: { style: false, default: '', only: 'site' },
  collapsibleGroups: { style: false, default: false, only: 'site' },
  crosshairHover: { style: false, default: false, only: 'site' },
};
